import { Locale } from './locale.model';
import countryObjects from './countries.json';
export type LocaleDomain = 'myStihl' | 'connected' | 'connected2' | 'stihlId' | 'notify';
export type CountryList = { [index: string]: Locale[] };

/**
 * List of available locales for each country in the following format: { [country]: [locales] }
 * @example { us: [Locale, Locale], de: [Locale, Locale] } where Locale is { language: 'en', country: 'us', defaultLanguage: true, region: 'EU' }
 * The reduce is used to convert the countryObjects to Locale objects
 */
export const countries: CountryList = Object.keys(countryObjects).reduce((result: { [key: string]: Locale[] }, key) => {
  const locales = countryObjects[key as keyof typeof countryObjects];
  result[key] = locales.map(
    (locale) => new Locale(locale.language, locale.country, locale.defaultLanguage, locale.region === 'US' ? 'US' : 'EU')
  );
  return result;
}, {});

function reduceByCountryList(countryList: string[]) {
  return Object.keys(countries)
    .filter((key) => new RegExp(`${countryList.join('|')}`).test(key))
    .reduce((cur, key) => {
      return Object.assign(cur, { [key]: countries[key] });
    }, {});
}

export const countriesByDomain: Record<LocaleDomain, CountryList> = {
  myStihl: reduceByCountryList([
    'us',
    'ca',
    'be',
    'de',
    'nl',
    'lu',
    'fr',
    'es',
    'no',
    'se',
    'dk',
    'fi',
    'gb',
    'pt',
    'it',
    'ch',
    'at',
    'pl',
    'lt',
    'au',
    'nz',
    'za',
    'ru',
    'br',
    'ar',
    'mx',
    'jp',
    'ro',
    'bg',
    'gr',
    'cy',
    'in',
    'al',
    'ba',
    'co',
    'hr',
    'cz',
    'ee',
    'hu',
    'ie',
    'lv',
    'mk',
    'mt',
    'rs',
    'sk',
    'si',
    'ua',
    'pe',
    'ke',
  ]),
  stihlId: reduceByCountryList([
    'us',
    'ca',
    'be',
    'de',
    'nl',
    'lu',
    'fr',
    'es',
    'no',
    'se',
    'dk',
    'fi',
    'gb',
    'pt',
    'it',
    'ch',
    'at',
    'pl',
    'lt',
    'au',
    'nz',
    'za',
    'ru',
    'br',
    'ar',
    'mx',
    'jp',
    'ro',
    'bg',
    'gr',
    'cy',
    'in',
    'al',
    'ba',
    'co',
    'hr',
    'cz',
    'ee',
    'hu',
    'ie',
    'lv',
    'mk',
    'mt',
    'rs',
    'sk',
    'si',
    'ua',
    'pe',
    'ke',
  ]),
  connected: reduceByCountryList([
    'us',
    'ca',
    'be',
    'de',
    'nl',
    'lu',
    'fr',
    'es',
    'no',
    'se',
    'dk',
    'fi',
    'gb',
    'pt',
    'it',
    'ch',
    'at',
    'pl',
    'lt',
    'au',
    'nz',
    'za',
  ]),
  connected2: reduceByCountryList([
    'us',
    'ca',
    'be',
    'de',
    'nl',
    'lu',
    'fr',
    'es',
    'no',
    'se',
    'dk',
    'fi',
    'gb',
    'pt',
    'it',
    'ch',
    'at',
    'pl',
    'lt',
    'au',
    'nz',
    'za',
  ]),
  notify: reduceByCountryList([
    'us',
    'ca',
    'be',
    'de',
    'nl',
    'lu',
    'fr',
    'es',
    'no',
    'se',
    'dk',
    'fi',
    'gb',
    'pt',
    'it',
    'ch',
    'at',
    'pl',
    'lt',
    'au',
    'nz',
    'za',
    'ru',
    'br',
    'ar',
    'mx',
    'jp',
    'ro',
    'bg',
    'gr',
    'cy',
    'in',
    'al',
    'ba',
    'co',
    'hr',
    'cz',
    'ee',
    'hu',
    'ie',
    'lv',
    'mk',
    'mt',
    'rs',
    'sk',
    'si',
    'ua',
    'pe',
    'ke',
  ]),
};

export function getAvailableLocales(domain: LocaleDomain) {
  const availableLocales: string[] = [];
  Object.values(countriesByDomain[domain])
    .map((locales) => locales.map((locale) => locale.toString()))
    .forEach((countries) => {
      availableLocales.push(...countries);
    });
  return availableLocales;
}

export function getAvailableLocalesAsRFC5646(domain: LocaleDomain) {
  const availableLocales: string[] = [];
  Object.values(countriesByDomain[domain])
    .map((locales) => locales.map((locale) => locale.RFC5646))
    .forEach((countries) => {
      availableLocales.push(...countries);
    });
  return availableLocales;
}

export const defaultLocale: Locale = countries['us']?.find((locale) => locale.defaultLanguage) ?? new Locale('en', 'us', true);
